import { Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAmountsStore } from "@/features/auth/amounts-store";
import { useAuthStore } from "@/features/auth/store";
import { can } from "@/lib/permissions/can";
import { cn } from "@/lib/utils";

/**
 * Confidentialité des montants : tous les montants de l'interface sont
 * masqués (`••••••`) par défaut à chaque session, en mémoire uniquement.
 * Seuls les titulaires de `finance.view_amounts` peuvent les ré-afficher, et
 * uniquement après re-saisie de leur mot de passe (fenêtre partagée
 * `AmountsUnlockDialog`). Sans la permission : pas de bouton, montants
 * toujours masqués. `className` permet de réutiliser le contrôle hors navbar
 * (ex. drawer mobile sur fond clair, cf. ThemeToggle).
 */
export function AmountsVisibilityToggle({ className }: { className?: string }) {
  const me = useAuthStore((s) => s.me);
  const visible = useAmountsStore((s) => s.visible);
  const setVisible = useAmountsStore((s) => s.setVisible);
  const requestUnlock = useAmountsStore((s) => s.requestUnlock);

  if (!can(me, "finance.view_amounts")) return null;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={visible ? "Masquer les montants" : "Afficher les montants"}
      title={visible ? "Masquer les montants" : "Afficher les montants"}
      className={cn(
        "text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground",
        className,
      )}
      onClick={() => (visible ? setVisible(false) : requestUnlock())}
    >
      {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </Button>
  );
}
